import i18next, { type i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  FALLBACK_LOCALE,
  LOCALES,
  LOCALE_DATE_TAGS,
  type LocaleCode,
  resolveLocale,
} from './locales';
import { NAMESPACES, en } from './resources';
import { gu } from './translations/gu';
import { hi } from './translations/hi';
import { mr } from './translations/mr';

/**
 * i18next for both apps (ARCHITECTURE §3 i18n, BP-01 §9, BP-19).
 *
 * Three core namespaces — `common`, `auth`, `settings` — bundled rather than
 * fetched so a tablet with no network still renders its own chrome. `common` is the
 * default namespace, so a component that names no namespace still resolves a key.
 * Every other namespace is added at import time by the feature that owns it, through
 * `registerBundle`.
 *
 * BP-19 adds the other three locales. `fallbackLng` is `en` and stays `en`: rule 1
 * says a key a locale has no entry for renders the English, which is precisely what
 * i18next does for free — so a partially translated namespace is a normal state of
 * this build, not a bug, and `missingKeyHandler` fires only when no locale at all,
 * `en` included, has the key.
 */

export { NAMESPACES, auth, common, en, settings } from './resources';
export type { Namespace } from './resources';
export {
  FALLBACK_LOCALE,
  LOCALES,
  LOCALE_DATE_TAGS,
  LOCALE_NAMES,
  deviceLocale,
  isLocale,
  resolveLocale,
} from './locales';
export type { LanguageSource, LocaleCode } from './locales';

/** The default namespace, used when a component calls `useTranslation()` bare. */
export const DEFAULT_NAMESPACE = 'common';

let instance: i18n | undefined;

/** `${lng}:${ns}:${key}` for every key already reported, so rule 1 logs once. */
const reported = new Set<string>();

/**
 * Rule 1's "missing key logs once and renders `en`".
 *
 * Reached only when `en` is missing the key too — with a `fallbackLng` of `en`, a key
 * absent from `hi` but present in `en` resolves and is never reported. Logged rather
 * than thrown: a missing string is a blemish on one screen, and crashing the salon's
 * till over it would be the larger bug. `pnpm i18n:check` is where it fails the gate.
 */
function reportMissing(languages: readonly string[], namespace: string, key: string): void {
  const slot = `${languages.join(',')}:${namespace}:${key}`;
  if (reported.has(slot)) {
    return;
  }
  reported.add(slot);
  console.warn(`i18n: no string for ${namespace}:${key} in ${languages.join(', ')}`);
}

/** Idempotent: both app shells and every test may ask for the instance. */
export function initI18n(): i18n {
  if (instance !== undefined) {
    return instance;
  }
  const created = i18next.createInstance();
  created.use(initReactI18next);
  void created.init({
    lng: FALLBACK_LOCALE,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: [...LOCALES],
    ns: [...NAMESPACES],
    defaultNS: DEFAULT_NAMESPACE,
    resources: { en, hi, mr, gu },
    interpolation: { escapeValue: false },
    saveMissing: true,
    missingKeyHandler: (languages, namespace, key) => {
      reportMissing(languages, namespace, key);
    },
  });
  instance = created;
  return created;
}

/**
 * Add one namespace in every locale that has it (BP-19.T1).
 *
 * The feature that owns a namespace calls this at import time with whatever locales it
 * carries; a locale it omits falls back to `en` per rule 1. `deep` is on so a feature
 * may register in two calls, and `overwrite` is off so the first registration of a key
 * wins — a test that re-imports a feature module must not silently change the strings
 * another test is asserting on.
 */
export function registerBundle(
  namespace: string,
  byLocale: Partial<Record<LocaleCode, object>>,
): void {
  const target = initI18n();
  for (const locale of LOCALES) {
    const bundle = byLocale[locale];
    if (bundle !== undefined) {
      target.addResourceBundle(locale, namespace, bundle, true, false);
    }
  }
}

/** Switch language. Every mounted component re-renders; nothing reloads. */
export function changeLocale(locale: LocaleCode): void {
  void initI18n().changeLanguage(locale);
}

/**
 * Which locale is rendering right now, narrowed to the four.
 *
 * `resolvedLanguage` and not `language`: i18next keeps `language` as asked even when
 * `supportedLngs` rejected it, and `resolveLocale` is what turns anything else —
 * including the `undefined` of an instance that has not finished initialising — into
 * `en`.
 */
export function currentLocale(): LocaleCode {
  return resolveLocale(initI18n().resolvedLanguage);
}

/**
 * The BCP-47 tag a date should be formatted with right now (BP-19 §2).
 *
 * A function and not a constant: it is read at render time, and a component that
 * calls `useTranslation` re-renders when the language changes, so the label follows
 * the switcher without anybody wiring a prop through.
 */
export function dateTag(): string {
  return LOCALE_DATE_TAGS[currentLocale()];
}

/** Test-only: drop the memoised instance so a fresh init can be observed. */
export function resetI18nForTests(): void {
  instance = undefined;
  reported.clear();
}
